import type { ComponentProps } from "react";
import type { ThemeProvider } from "@mui/material/styles";

// TAKEN FROM `ThemeProvider`'S OWN PROPS: THE TYPE LIVES IN `@mui/system`, WHICH
// IS ONLY A TRANSITIVE DEPENDENCY HERE
type StorageManager = NonNullable<ComponentProps<typeof ThemeProvider>["storageManager"]>;

// THE LIGHT/DARK CHOICE IS STORED WHERE THE ANGULAR APP KEEPS IT: `colorScheme`
// ("light" | "dark") INSIDE THE `APP_CONFIG` JSON THAT `LayoutService` PERSISTS,
// ALONGSIDE ITS `theme` ("pillars-light" | "pillars-dark"). BOTH APPS ON ONE
// ORIGIN THEREFORE AGREE ON THE USER'S CHOICE. THE OTHER `APP_CONFIG` FIELDS ARE
// ANGULAR'S AND ARE PRESERVED UNTOUCHED.
export const APP_CONFIG_KEY = "APP_CONFIG";
export const MODE_STORAGE_KEY = "pillars-color-mode";

export type ColorMode = "light" | "dark";

function readConfig(): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(APP_CONFIG_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

// ANGULAR DEFAULTS TO LIGHT (NOT THE OS SETTING), SO THE PILOT DOES TOO
export function storedColorMode(): ColorMode {
  return readConfig()["colorScheme"] === "dark" ? "dark" : "light";
}

function writeColorMode(mode: ColorMode): void {
  try {
    const config = readConfig();
    localStorage.setItem(
      APP_CONFIG_KEY,
      JSON.stringify({ ...config, colorScheme: mode, theme: mode === "dark" ? "pillars-dark" : "pillars-light" }),
    );
  } catch {
    // STORAGE UNAVAILABLE - THE CHOICE LASTS FOR THIS PAGE ONLY
  }
}

// MUI ASKS ITS STORAGE MANAGER FOR THREE KEYS: THE MODE, AND THE COLOR SCHEME
// NAMES TO USE FOR LIGHT AND DARK. ONLY THE MODE IS PERSISTED; THE SCHEME NAMES
// ARE FIXED ("light"/"dark") SO THEY FALL BACK TO THEIR DEFAULTS.
export const appConfigStorageManager: StorageManager = ({ key }) => ({
  get: (defaultValue) => (key === MODE_STORAGE_KEY ? storedColorMode() : defaultValue),
  set: (value) => {
    if (key === MODE_STORAGE_KEY && (value === "light" || value === "dark")) writeColorMode(value);
  },
  // FOLLOW A CHANGE MADE IN ANOTHER TAB (OR BY THE ANGULAR APP ON THE SAME ORIGIN)
  subscribe: (handler) => {
    if (key !== MODE_STORAGE_KEY) return () => {};
    const onStorage = (e: StorageEvent) => {
      if (e.key === APP_CONFIG_KEY) handler(storedColorMode());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  },
});
